import { useState, useMemo, useCallback } from 'react';
import { RefreshCw, Database, Filter, FilterX, ArrowUp, ArrowDown } from 'lucide-react';
import type { FabricData, DatabaseSummary } from '../types';
import { DatabaseRow } from './DatabaseRow';

type SortColumn =
  | 'workspace_name'
  | 'mirrored_db_name'
  | 'mirroring_status'
  | 'number_of_tables'
  | 'number_of_rows_replicated'
  | 'gigs_processed'
  | 'number_of_tables_in_failed_state'
  | 'lastSyncDateTime';

interface DatabaseTableProps {
  data: FabricData | null;
  onRefreshAll: () => void;
  onRefreshDatabase: (dbId: string) => void;
  refreshingDatabases: Set<string>;
  loading: boolean;
}

interface ColumnDef {
  key: SortColumn;
  label: string;
  align?: 'left' | 'right';
}

const columns: ColumnDef[] = [
  { key: 'workspace_name', label: 'Workspace', align: 'left' },
  { key: 'mirrored_db_name', label: 'Database', align: 'left' },
  { key: 'mirroring_status', label: 'Status', align: 'left' },
  { key: 'number_of_tables', label: 'Tables', align: 'right' },
  { key: 'number_of_rows_replicated', label: 'Rows', align: 'right' },
  { key: 'gigs_processed', label: 'GB', align: 'right' },
  { key: 'number_of_tables_in_failed_state', label: 'Failed', align: 'right' },
  { key: 'lastSyncDateTime', label: 'Last Sync', align: 'left' },
];

function compareDatabases(
  a: DatabaseSummary,
  b: DatabaseSummary,
  column: SortColumn,
  ascending: boolean
): number {
  let result = 0;

  switch (column) {
    case 'workspace_name':
      result = a.workspace_name.localeCompare(b.workspace_name);
      break;
    case 'mirrored_db_name':
      result = a.mirrored_db_name.localeCompare(b.mirrored_db_name);
      break;
    case 'mirroring_status':
      result = a.mirroring_status.localeCompare(b.mirroring_status);
      break;
    case 'number_of_tables':
      result = a.number_of_tables - b.number_of_tables;
      break;
    case 'number_of_rows_replicated':
      result = a.number_of_rows_replicated - b.number_of_rows_replicated;
      break;
    case 'gigs_processed':
      result = a.gigs_processed - b.gigs_processed;
      break;
    case 'number_of_tables_in_failed_state':
      result =
        a.number_of_tables_in_failed_state -
        b.number_of_tables_in_failed_state;
      break;
    case 'lastSyncDateTime': {
      const aTime = a.lastSyncDateTime
        ? new Date(a.lastSyncDateTime).getTime()
        : 0;
      const bTime = b.lastSyncDateTime
        ? new Date(b.lastSyncDateTime).getTime()
        : 0;
      result = aTime - bTime;
      break;
    }
  }

  return ascending ? result : -result;
}

export function DatabaseTable({
  data,
  onRefreshAll,
  onRefreshDatabase,
  refreshingDatabases,
  loading,
}: DatabaseTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('mirrored_db_name');
  const [sortAscending, setSortAscending] = useState(true);
  const [expandedDatabases, setExpandedDatabases] = useState<Set<string>>(
    new Set()
  );
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (sortColumn === column) {
        setSortAscending((prev) => !prev);
      } else {
        setSortColumn(column);
        setSortAscending(true);
      }
    },
    [sortColumn]
  );

  const toggleExpanded = useCallback((dbId: string) => {
    setExpandedDatabases((prev) => {
      const next = new Set(prev);
      if (next.has(dbId)) {
        next.delete(dbId);
      } else {
        next.add(dbId);
      }
      return next;
    });
  }, []);

  const sortedDatabases = useMemo(() => {
    if (!data?.summary) return [];

    let databases = [...data.summary];

    if (showErrorsOnly) {
      databases = databases.filter(
        (db) => db.number_of_tables_in_failed_state > 0
      );
    }

    databases.sort((a, b) =>
      compareDatabases(a, b, sortColumn, sortAscending)
    );

    return databases;
  }, [data, sortColumn, sortAscending, showErrorsOnly]);

  const getTablesForDatabase = useCallback(
    (dbId: string) => {
      if (!data?.tables) return [];
      return data.tables.filter((t) => t.mirrored_database_id === dbId);
    },
    [data]
  );

  const hasData = data?.summary && data.summary.length > 0;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-800/60">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowErrorsOnly((prev) => !prev)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-600 ${
              showErrorsOnly
                ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300'
            }`}
          >
            {showErrorsOnly ? (
              <>
                <FilterX className="w-4 h-4" />
                Show All
              </>
            ) : (
              <>
                <Filter className="w-4 h-4" />
                Show Errors Only
              </>
            )}
          </button>
        </div>

        <button
          onClick={onRefreshAll}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
        >
          <RefreshCw
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh All
        </button>
      </div>

      {/* Table */}
      {hasData ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-800/60 bg-gray-900/80">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-300 transition-colors duration-100 ${
                      col.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        col.align === 'right' ? 'justify-end' : ''
                      }`}
                    >
                      <span>{col.label}</span>
                      {sortColumn === col.key && (
                        sortAscending ? (
                          <ArrowUp className="w-3 h-3 text-blue-400" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-blue-400" />
                        )
                      )}
                    </div>
                  </th>
                ))}
                {/* Actions column */}
                <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-16">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDatabases.map((db) => (
                <DatabaseRow
                  key={db.mirrored_database_id}
                  database={db}
                  tables={getTablesForDatabase(db.mirrored_database_id)}
                  isExpanded={expandedDatabases.has(db.mirrored_database_id)}
                  onToggle={() => toggleExpanded(db.mirrored_database_id)}
                  onRefresh={() =>
                    onRefreshDatabase(db.mirrored_database_id)
                  }
                  isRefreshing={refreshingDatabases.has(
                    db.mirrored_database_id
                  )}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-14 h-14 rounded-2xl bg-gray-800/60 border border-gray-700/40 flex items-center justify-center mb-4">
            <Database className="w-7 h-7 text-gray-500" />
          </div>
          <p className="text-sm text-gray-400 text-center max-w-sm">
            {loading
              ? 'Loading monitoring data...'
              : 'No data available. Click Refresh All to load monitoring data.'}
          </p>
        </div>
      )}
    </div>
  );
}
